import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '../context/ToastContext';
import { useDatabase } from '../context/DatabaseContext';
import {
    getSubAgentDefinition,
    generateQueriesFromInsights,
    getInsightsForAgent,
    generateInsightFromNLP,
    deleteInsight,
    executeInsightsQueries,
    getSubAgents
} from '../services/agentApi';
import ConfirmationModal from '../components/ui/ConfirmationModal';
import { ParameterInputModal } from '../components/agents/ParameterInputModal';
import { ParametersModal } from '../components/ui/ParametersModal';
import {
    Lightbulb,
    CheckCircle,
    Loader,
    ArrowLeft,
    Sparkles,
    Clock,
    Code,
    Plus,
    X,
    Trash2,
    FileText,
    TrendingUp,
    Shield,
    Package,
    Users,
    Activity,
    Bell,
    MessageCircle,
    BarChart3,
    AlertCircle,
    AlertTriangle,
    ChevronDown,
    ChevronUp,
    DollarSign,
    Target,
    Info,
    CreditCard,
    Megaphone,
    Utensils,
    Truck,
    Layers,
    ShoppingCart
} from 'lucide-react';

// Dynamic iframe component - expands to full content height and scrolls with the page
const DynamicIframe = ({ htmlContent }) => {
    const { t } = useTranslation('page-agent-insights-selection');
    const iframeRef = useRef(null);
    const [height, setHeight] = useState(600);
    const frameId = useRef(`frame_${Math.random().toString(36).slice(2)}`);

    const injectedContent = useMemo(() => {
        if (!htmlContent) return '';
        const id = frameId.current;
        const script = `<script>
(function(){
  function sendHeight(){
    var h = Math.max(
      document.body ? document.body.scrollHeight : 0,
      document.documentElement ? document.documentElement.scrollHeight : 0
    );
    window.parent.postMessage({ type: 'iframeHeight', frameId: '${id}', height: h }, '*');
  }
  window.addEventListener('load', function(){ sendHeight(); setTimeout(sendHeight, 300); setTimeout(sendHeight, 1000); setTimeout(sendHeight, 2500); });
  document.addEventListener('DOMContentLoaded', sendHeight);
  var mo = new MutationObserver(sendHeight);
  document.addEventListener('DOMContentLoaded', function(){
    mo.observe(document.body, { childList: true, subtree: true });
  });
})();
</script>`;
        if (htmlContent.includes('</body>')) {
            return htmlContent.replace('</body>', script + '</body>');
        }
        return htmlContent + script;
    }, [htmlContent]);

    useEffect(() => {
        const id = frameId.current;
        const handleMessage = (event) => {
            if (event.data && event.data.type === 'iframeHeight' && event.data.frameId === id) {
                const newHeight = Math.max(Number(event.data.height) + 32, 400);
                setHeight(newHeight);
            }
        };
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, []);

    return (
        <iframe
            ref={iframeRef}
            srcDoc={injectedContent}
            title={t('report.iframeTitle')}
            className="w-full border-none bg-white block"
            style={{ height: `${height}px`, overflow: 'hidden', display: 'block' }}
            sandbox="allow-scripts"
        />
    );
};

/**
 * AgentInsightsSelection - Page for selecting insights to investigate.
 * Shows AI-discovered insights with "Why It Matters" sections and "Generate Queries" buttons.
 * Features a Sub-Agent Navigator sidebar showing all sub-agents.
 * Includes tabs for switching between Insights List and Sub Agent Report.
 */
export const AgentInsightsSelection = () => {
    const { t, i18n } = useTranslation(['page-agent-insights-selection', 'common']);
    const dateLocale = i18n.language?.startsWith('ja') ? 'ja-JP' : 'en-GB';
    const navigate = useNavigate();
    const location = useLocation();
    const { agentId } = useParams();
    const { showToast } = useToast();
    const { databases } = useDatabase();

    const [activeTab, setActiveTab] = useState('insights');

    const [loading, setLoading] = useState(false);
    const [loadingQueries, setLoadingQueries] = useState(false);
    const [generatingInsightIds, setGeneratingInsightIds] = useState([]);
    const [insights, setInsights] = useState([]);
    const [selectedInsightIds, setSelectedInsightIds] = useState([]);
    const [queryCountsMap, setQueryCountsMap] = useState({});

    // Current agent state
    const [currentAgent, setCurrentAgent] = useState(null);

    // Sub-agents for the navigator sidebar
    const [subAgents, setSubAgents] = useState([]);
    const [loadingSubAgents, setLoadingSubAgents] = useState(false);
    const [insightsCountMap, setInsightsCountMap] = useState({});

    // NLP Insight generation modal state
    const [showInsightNLPModal, setShowInsightNLPModal] = useState(false);
    const [insightNLPInput, setInsightNLPInput] = useState('');
    const [generatingInsightNLP, setGeneratingInsightNLP] = useState(false);

    // Delete insight confirmation modal state
    const [deleteInsightConfirmModal, setDeleteInsightConfirmModal] = useState({
        isOpen: false,
        insightId: null,
    });

    // Execute queries modal state
    const [showExecuteModal, setShowExecuteModal] = useState(false);
    const [executingQueries, setExecutingQueries] = useState(false);
    const [queriesToExecute, setQueriesToExecute] = useState([]);

    // Use sessionStorage to persist databaseId across navigation
    const storedDbId = sessionStorage.getItem(`agent_${agentId}_databaseId`);
    const [databaseId, setDatabaseId] = useState(() => {
        return storedDbId ? parseInt(storedDbId) : null;
    });

    // Persist databaseId to sessionStorage when it changes
    useEffect(() => {
        if (databaseId) {
            sessionStorage.setItem(`agent_${agentId}_databaseId`, databaseId.toString());
        }
    }, [agentId, databaseId]);

    const agentNameFromState = location.state?.agentName;
    // Use agent name from state or fetched agent data
    const displayAgentName = agentNameFromState || currentAgent?.name || 'Agent';

    // Handle sub-agent click in navigator
    const handleSubAgentClick = useCallback((agent) => {
        const dbId = agent.database_connection_id;
        if (dbId) {
            setDatabaseId(dbId);
            sessionStorage.setItem(`agent_${agent.id}_databaseId`, dbId.toString());
        }
        navigate(`/agents/insights/${agent.id}`, {
            state: { agentName: agent.name }
        });
    }, [navigate]);

    // Get agent icon based on name/type
    const getAgentIcon = useCallback((agentName) => {
        const name = agentName.toLowerCase();
        
        // 1. Payment & Conversion
        if (name.includes('payment') || name.includes('conversion') || name.includes('transaction') || name.includes('checkout')) {
            return { icon: CreditCard, bg: 'bg-primary-100', color: 'text-primary-600' };
        }
        
        // 2. Marketing
        if (name.includes('marketing') || name.includes('campaign') || name.includes('promotion') || name.includes('coupon') || name.includes('offer')) {
            return { icon: Megaphone, bg: 'bg-purple-100', color: 'text-purple-600' };
        }
        
        // 3. Restaurant
        if (name.includes('restaurant') || name.includes('food') || name.includes('dining') || name.includes('menu')) {
            return { icon: Utensils, bg: 'bg-orange-100', color: 'text-orange-600' };
        }
        
        // 4. Delivery
        if (name.includes('delivery') || name.includes('logistics') || name.includes('route') || name.includes('shipping') || name.includes('driver')) {
            return { icon: Truck, bg: 'bg-blue-100', color: 'text-blue-600' };
        }
        
        // 5. Inventory & Stock (KEEP AS IS)
        if (name.includes('inventory') || name.includes('stock') || name.includes('warehouse')) {
            return { icon: Package, bg: 'bg-orange-100', color: 'text-orange-600' };
        }
        
        // 6. Product Catalog
        if (name.includes('product') || name.includes('catalog') || name.includes('sku') || name.includes('category')) {
            return { icon: Layers, bg: 'bg-orange-100', color: 'text-orange-600' };
        }
        
        // 7. Customer Insights (KEEP AS IS)
        if (name.includes('customer') || name.includes('behavior') || name.includes('user') || name.includes('demographic') || name.includes('loyalty')) {
            return { icon: Users, bg: 'bg-primary-100', color: 'text-primary-600' };
        }
        
        // 8. Order Performance
        if (name.includes('order') || name.includes('sales')) {
            return { icon: ShoppingCart, bg: 'bg-orange-100', color: 'text-orange-600' };
        }

        // Defaults and others
        if (name.includes('trend') || name.includes('financial') || name.includes('cost')) return { icon: TrendingUp, bg: 'bg-primary-100', color: 'text-primary-600' };
        if (name.includes('optimizer')) return { icon: Package, bg: 'bg-orange-100', color: 'text-orange-600' };
        if (name.includes('vendor') || name.includes('supplier')) return { icon: Users, bg: 'bg-primary-100', color: 'text-primary-600' };
        if (name.includes('anomaly') || name.includes('security') || name.includes('fraud') || name.includes('alert') || name.includes('detection')) return { icon: Shield, bg: 'bg-red-100', color: 'text-red-600' };
        if (name.includes('activity') || name.includes('engagement') || name.includes('api') || name.includes('usage')) return { icon: Activity, bg: 'bg-cyan-100', color: 'text-cyan-600' };
        
        return { icon: Bell, bg: 'bg-gray-100', color: 'text-gray-600' };
    }, []);

    // Fetch sub-agents for the navigator sidebar
    const fetchSubAgents = useCallback(async () => {
        if (!databaseId) return;

        setLoadingSubAgents(true);
        try {
            const response = await getSubAgents(databaseId);
            if (response.data?.data?.sub_agents) {
                setSubAgents(response.data.data.sub_agents);
            }
        } catch (error) {
            console.error('Failed to fetch sub-agents:', error);
        } finally {
            setLoadingSubAgents(false);
        }
    }, [databaseId]);

    // Fetch insights count for each sub-agent
    const fetchInsightsCounts = useCallback(async (agents) => {
        if (!databaseId || agents.length === 0) return;

        try {
            const countsMap = {};
            for (const agent of agents) {
                try {
                    const insightsRes = await getInsightsForAgent(agent.id, databaseId);
                    if (insightsRes.data?.status === 'success') {
                        countsMap[agent.id] = insightsRes.data.data?.insights?.length || 0;
                    }
                } catch (error) {
                    console.debug(`Could not fetch insights for agent ${agent.id}:`, error);
                    countsMap[agent.id] = 0;
                }
            }
            setInsightsCountMap(countsMap);
        } catch (error) {
            console.error('Failed to fetch insights counts:', error);
        }
    }, [databaseId]);

    // Fetch sub-agents when databaseId is available
    useEffect(() => {
        if (databaseId) {
            fetchSubAgents();
        }
    }, [databaseId, fetchSubAgents]);

    // Fetch current agent details when agentId changes
    useEffect(() => {
        if (!agentId) return;

        getSubAgentDefinition(agentId)
            .then((response) => {
                if (response.data?.status === 'success') {
                    const agent = response.data.data;
                    setCurrentAgent(agent);
                    if (!databaseId && agent.database_connection_id) {
                        const dbId = agent.database_connection_id;
                        setDatabaseId(dbId);
                        sessionStorage.setItem(`agent_${agentId}_databaseId`, dbId.toString());
                    }
                }
            })
            .catch((error) => {
                console.error('Failed to fetch agent details:', error);
            });
    }, [agentId, databaseId]);

    // Fetch insights counts after sub-agents are loaded
    useEffect(() => {
        if (subAgents.length > 0) {
            fetchInsightsCounts(subAgents);
        }
    }, [subAgents, fetchInsightsCounts]);

    // Fetch agent insights
    const fetchInsights = useCallback(async () => {
        if (!agentId || !databaseId) return;

        setLoading(true);
        try {
            const insightsRes = await getInsightsForAgent(agentId, databaseId);
            if (insightsRes.data?.status === 'success') {
                const insightsData = insightsRes.data.data?.insights || [];
                setInsights(insightsData);
                setSelectedInsightIds([]);
            }
        } catch (error) {
            console.error('Failed to fetch insights:', error);
            showToast(t('toast.loadInsightsFailed'), 'error');
        } finally {
            setLoading(false);
        }
    }, [agentId, databaseId, showToast, t]);

    useEffect(() => {
        if (agentId && databaseId) {
            fetchInsights();
        }
    }, [agentId, databaseId, fetchInsights]);

    // Extract query counts from insights
    useEffect(() => {
        if (insights.length > 0) {
            const countsMap = {};
            for (const insight of insights) {
                countsMap[insight.id] = insight.generated_queries?.length || 0;
            }
            setQueryCountsMap(countsMap);
        }
    }, [insights]);

    const toggleInsightSelection = (insightId) => {
        setSelectedInsightIds(prev =>
            prev.includes(insightId)
                ? prev.filter(id => id !== insightId)
                : [...prev, insightId]
        );
    };

    const handleSelectAll = () => {
        setSelectedInsightIds(insights.map(i => i.id));
    };

    const handleDeselectAll = () => {
        setSelectedInsightIds([]);
    };

    const handleHighPriorityOnly = () => {
        const highPriorityIds = insights
            .filter(i => i.priority === 'HIGH')
            .map(i => i.id);
        setSelectedInsightIds(highPriorityIds);
    };

    const handleGenerateQueries = async (idsToGenerate) => {
        const ids = idsToGenerate || selectedInsightIds;
        if (ids.length === 0) {
            showToast(t('toast.selectAtLeastOne'), 'error');
            return;
        }

        setLoadingQueries(true);
        setGeneratingInsightIds(ids);
        try {
            const response = await generateQueriesFromInsights(ids);
            if (response.data?.status === 'success') {
                showToast(t('toast.queriesGenerated'), 'success');
                navigate(`/agents/queries/${agentId}`, {
                    state: {
                        agentName: displayAgentName,
                        selectedInsightIds: ids,
                        databaseId: databaseId
                    }
                });
            }
        } catch (error) {
            console.error('Failed to generate queries:', error);
            showToast(error.response?.data?.detail || t('toast.generateQueriesFailed'), 'error');
        } finally {
            setLoadingQueries(false);
            setGeneratingInsightIds([]);
        }
    };

    // Execute ALL queries from all insights for this sub-agent
    const handleExecuteAllClick = async () => {
        setLoading(true);
        try {
            const insightsRes = await getInsightsForAgent(agentId, databaseId);
            if (insightsRes.data?.status === 'success') {
                const insightsData = insightsRes.data.data?.insights || [];

                const allQueries = [];
                const allInsightIds = [];

                insightsData.forEach(insight => {
                    allInsightIds.push(insight.id);
                    if (insight.generated_queries && insight.generated_queries.length > 0) {
                        allQueries.push(...insight.generated_queries);
                    }
                });

                if (allQueries.length === 0) {
                    showToast(t('toast.noQueriesFound'), 'error');
                    return;
                }

                setQueriesToExecute(allQueries);
                setSelectedInsightIds(allInsightIds);
                setShowExecuteModal(true);
            }
        } catch (error) {
            console.error('Failed to fetch queries:', error);
            showToast(t('toast.fetchQueriesFailed'), 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        if (databaseId) {
            navigate(`/agents/${databaseId}`);
        } else {
            navigate('/agents');
        }
    };

    // NLP Insight generation handlers
    const openInsightNLPModal = () => {
        setInsightNLPInput('');
        setShowInsightNLPModal(true);
    };

    const handleInsightNLPInputChange = (e) => {
        setInsightNLPInput(e.target.value);
        const textarea = e.target;
        textarea.style.height = 'auto';
        textarea.style.height = Math.min(textarea.scrollHeight, 300) + 'px';
        textarea.style.overflowY = textarea.scrollHeight > 300 ? 'auto' : 'hidden';
    };

    const closeInsightNLPModal = () => {
        setShowInsightNLPModal(false);
        setInsightNLPInput('');
    };

    const handleGenerateInsightFromNLP = async () => {
        if (!insightNLPInput.trim()) {
            showToast(t('toast.describeInsightRequired'), 'error');
            return;
        }

        setGeneratingInsightNLP(true);
        try {
            const response = await generateInsightFromNLP(agentId, insightNLPInput);

            if (response.data?.status === 'success') {
                showToast(t('toast.insightGenerated'), 'success');
                closeInsightNLPModal();
                const newInsight = response.data.data?.insight;
                if (newInsight) {
                    setInsights(prev => [newInsight, ...prev]);
                }
            }
        } catch (error) {
            console.error('Failed to generate insight from NLP:', error);
            showToast(error.response?.data?.detail || t('toast.generateInsightFailed'), 'error');
        } finally {
            setGeneratingInsightNLP(false);
        }
    };

    // Delete insight handlers
    const openDeleteInsightModal = (insight) => {
        setDeleteInsightConfirmModal({
            isOpen: true,
            insightId: insight.id,
        });
    };

    const closeDeleteInsightModal = () => {
        setDeleteInsightConfirmModal(prev => ({ ...prev, isOpen: false, insightId: null }));
    };

    const confirmDeleteInsight = async () => {
        if (!deleteInsightConfirmModal.insightId) return;

        try {
            await deleteInsight(deleteInsightConfirmModal.insightId);
            showToast(t('toast.insightDeleted'), 'success');
            closeDeleteInsightModal();
            setInsights(prev => prev.filter(i => i.id !== deleteInsightConfirmModal.insightId));
        } catch (error) {
            console.error('Failed to delete insight:', error);
            showToast(error.response?.data?.detail || t('toast.deleteInsightFailed'), 'error');
        }
    };

    const handleExecuteAllQueries = async (parametersByQuery) => {
        setExecutingQueries(true);
        try {
            const response = await executeInsightsQueries(
                agentId,
                databaseId,
                selectedInsightIds,
                parametersByQuery
            );

            if (response.data?.status === 'success') {
                showToast(t('toast.queriesExecuted'), 'success');
                setShowExecuteModal(false);

                // Switch to the report tab to show the generated report
                setActiveTab('report');

                // Increment refresh key to trigger re-fetch of the report
                setReportRefreshKey(prev => prev + 1);

                // Refresh insights to get the latest data
                await fetchInsights();
            }
        } catch (error) {
            console.error('Failed to execute queries:', error);
            showToast(error.response?.data?.detail || t('toast.executeQueriesFailed'), 'error');
        } finally {
            setExecutingQueries(false);
        }
    };

    // Get priority badge styling
    const getPriorityBadge = (priority) => {
        switch (priority?.toUpperCase()) {
            case 'HIGH':
                return { bg: 'bg-red-50', text: 'text-red-800', border: 'border-red-200', label: t('priority.high') };
            case 'MEDIUM':
                return { bg: 'bg-yellow-50', text: 'text-yellow-800', border: 'border-yellow-200', label: t('priority.medium') };
            default:
                return { bg: 'bg-primary-50', text: 'text-primary-800', border: 'border-primary-200', label: t('priority.low') };
        }
    };

    // Get category badge styling
    const getCategoryBadge = (category) => {
        if (!category) return null;
        const cat = category.toUpperCase();
        if (cat === 'HEALTH' || cat === 'SECURITY')
            return { bg: 'bg-primary-50', text: 'text-primary-800', border: 'border-primary-200', label: category };
        if (cat === 'DISTRIBUTION' || cat === 'PATTERN')
            return { bg: 'bg-primary-50', text: 'text-primary-800', border: 'border-primary-200', label: category };
        if (cat === 'QUERY')
            return { bg: 'bg-green-50', text: 'text-green-800', border: 'border-green-200', label: category };
        return { bg: 'bg-gray-50', text: 'text-gray-800', border: 'border-gray-200', label: category };
    };

    // State to trigger refresh of SubAgentReportTab
    const [reportRefreshKey, setReportRefreshKey] = useState(0);

    return (
        <div className="flex gap-theme">
            {/* Sub-Agent Navigator Sidebar */}
            <div className="w-[320px] flex-shrink-0 flex flex-col max-h-[calc(100vh-6rem)] sticky top-0">
                <div className="bg-white border border-gray-100 rounded-2xl shadow-[0_2px_10px_-3px_rgba(0,0,0,0.05)] flex flex-col flex-1 h-full overflow-hidden">
                    <div className="pt-6 px-6 pb-4">
                        <h2 className="text-base font-bold text-gray-900">{t('navigator.title')}</h2>
                    </div>

                    <div className="px-5 pb-4 overflow-y-auto space-y-3 flex-1 custom-scrollbar">
                        {loadingSubAgents ? (
                            <div className="flex justify-center py-8">
                                <Loader className="w-6 h-6 animate-spin text-primary-600" />
                            </div>
                        ) : subAgents.length === 0 ? (
                            <div className="text-center py-8 text-gray-500 text-sm">
                                {t('navigator.noSubAgents')}
                            </div>
                        ) : (
                            subAgents.map((agent) => {
                                const agentIconConfig = getAgentIcon(agent.name);
                                const AgentIcon = agentIconConfig.icon;
                                const isSelected = agent.id === parseInt(agentId);
                                const insightsCount = insightsCountMap[agent.id] || 0;

                                return (
                                    <div
                                        key={agent.id}
                                        onClick={() => handleSubAgentClick(agent)}
                                        className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${
                                            isSelected
                                                ? 'border-primary-200 bg-primary-50/40 shadow-sm'
                                                : 'border-gray-100 bg-white hover:border-gray-200 hover:bg-gray-50'
                                        }`}
                                    >
                                        <div className="flex items-start justify-between mb-3">
                                            <div className={`w-9 h-9 ${agentIconConfig.bg} rounded-lg flex items-center justify-center`}>
                                                <AgentIcon className={`w-4.5 h-4.5 ${agentIconConfig.color}`} />
                                            </div>
                                            <div className="flex items-center px-2 py-0.5 border border-gray-200 rounded-full bg-white shadow-sm">
                                                <Activity className={`w-3 h-3 mr-1 ${insightsCount > 0 ? 'text-primary-500' : 'text-gray-400'}`} />
                                                <span className={`text-xs font-bold ${insightsCount > 0 ? 'text-primary-600' : 'text-gray-500'}`}>
                                                    {insightsCount.toString().padStart(2, '0')}
                                                </span>
                                            </div>
                                        </div>
                                        <h3 className={`font-semibold text-sm leading-tight mb-1.5 ${isSelected ? 'text-gray-900' : 'text-gray-800'}`}>
                                            {agent.name}
                                        </h3>
                                        <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">
                                            {agent.description || t('navigator.defaultDescription', { focus: agent.focus || t('navigator.dataFallback') })}
                                        </p>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Chat with Super Agent */}
                    <div className="p-4 mt-auto">
                        <div className="p-4 bg-primary-50 rounded-xl border border-primary-200">
                            <div className="flex items-center gap-3 mb-3 text-primary-700">
                                <MessageCircle className="w-5 h-5 flex-shrink-0" />
                                <span className="text-sm font-medium leading-tight">{t('navigator.chatCta')}</span>
                            </div>
                            <button
                                onClick={() => {
                                    const db = databases.find(d => d.id === parseInt(databaseId));
                                    navigate(`/agents/chat/${databaseId}`, {
                                        state: { database: db }
                                    });
                                }}
                                className="w-full px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium text-sm transition-all shadow-sm flex items-center justify-center space-x-2"
                            >
                                <MessageCircle className="w-4 h-4" />
                                <span>{t('navigator.chatButton')}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 min-w-0 flex flex-col overflow-hidden max-h-[calc(100vh-6rem)]">
                {/* Header Section */}
                <div className="flex items-start justify-between mb-8 flex-shrink-0">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={handleBack}
                            className="w-10 h-10 bg-white border border-gray-200 rounded-full flex items-center justify-center hover:bg-gray-50 transition-colors shadow-sm flex-shrink-0"
                        >
                            <ArrowLeft className="w-5 h-5 text-gray-500" />
                        </button>
                        <div>
                            <h1 className="text-xl font-bold text-gray-900 leading-tight">
                                {displayAgentName}
                            </h1>
                            <p className="text-sm text-gray-500 mt-1">
                                {t('header.subtitle')}
                            </p>
                        </div>
                    </div>
                    {/* Tabs on right */}
                    <div className="bg-gray-100/80 p-1 rounded-lg flex items-center flex-shrink-0">
                        <button
                            onClick={() => setActiveTab('insights')}
                            className={`px-6 py-2 rounded-md text-sm font-semibold transition-all ${activeTab === 'insights' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            {t('header.tabInsights')}
                        </button>
                        <button
                            onClick={() => setActiveTab('report')}
                            className={`px-6 py-2 rounded-md text-sm font-semibold transition-all ${activeTab === 'report' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            {t('header.tabReport')}
                        </button>
                    </div>
                </div>

                {/* Tab Content */}
                <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
                    {activeTab === 'insights' ? (
                        <>
                            {/* Quick Select Controls */}
                            <div className="flex items-center justify-between mb-6 sticky top-0 bg-[#F7F8FB] z-10 pb-4 pt-2">
                                <div className="flex items-center space-x-3">
                                    <span className="text-sm font-bold text-gray-800">{t('quickSelect.label')}</span>
                                    <button
                                        onClick={handleSelectAll}
                                        className="px-4 py-2 text-xs font-medium border border-gray-200 text-gray-600 bg-white rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
                                        disabled={loading || insights.length === 0}
                                    >
                                        {t('quickSelect.selectAll')}
                                    </button>
                                    <button
                                        onClick={handleDeselectAll}
                                        className="px-4 py-2 text-xs font-medium border border-gray-200 text-gray-600 bg-white rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
                                        disabled={selectedInsightIds.length === 0}
                                    >
                                        {t('quickSelect.deselectAll')}
                                    </button>
                                    <button
                                        onClick={handleHighPriorityOnly}
                                        className="px-4 py-2 text-xs font-medium border border-gray-200 text-gray-600 bg-white rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
                                        disabled={loading || insights.length === 0}
                                    >
                                        {t('quickSelect.highPriorityOnly')}
                                    </button>
                                </div>
                                <div className="flex items-center space-x-4">
                                    <span className="text-sm text-gray-600 font-medium">
                                        {t('quickSelect.selectedCount', { selected: selectedInsightIds.length, total: insights.length })}
                                    </span>
                                    <button
                                        onClick={handleExecuteAllClick}
                                        disabled={executingQueries}
                                        className={`px-5 py-2.5 rounded-lg font-bold text-sm flex items-center space-x-2 transition-all shadow-sm ${
                                            executingQueries
                                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                                                : 'bg-green-100 text-[#059669] border border-green-300 hover:bg-green-200'
                                        }`}
                                    >
                                        {executingQueries ? (
                                            <>
                                                <Loader className="w-4 h-4 animate-spin" />
                                                <span>{t('quickSelect.executing')}</span>
                                            </>
                                        ) : (
                                            <>
                                                <FileText className="w-4 h-4" />
                                                <span>{t('quickSelect.generateReport')}</span>
                                            </>
                                        )}
                                    </button>
                                    <button
                                        onClick={openInsightNLPModal}
                                        className="px-5 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium text-sm flex items-center gap-2 transition-colors shadow-sm"
                                    >
                                        <Plus className="w-4 h-4" />
                                        <span>{t('quickSelect.addInsights')}</span>
                                    </button>
                                </div>
                            </div>

                            {/* Insights List */}
                            <div className="pb-8">
                                {loading ? (
                                    <div className="flex justify-center py-12">
                                        <Loader className="w-8 h-8 animate-spin text-primary-600" />
                                    </div>
                                ) : insights.length === 0 ? (
                                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                                        <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                            <Lightbulb className="w-12 h-12 text-gray-400" />
                                        </div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('emptyInsights.title')}</h3>
                                        <p className="text-gray-600 mb-6 max-w-md mx-auto">
                                            {t('emptyInsights.description', { agentName: displayAgentName })}
                                        </p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {insights.map((insight) => {
                                            const priorityBadge = getPriorityBadge(insight.priority);
                                            const categoryBadge = getCategoryBadge(insight.category);
                                            const isSelected = selectedInsightIds.includes(insight.id);
                                            const queryCount = queryCountsMap[insight.id] || 0;
                                            const hasQueries = queryCount > 0;

                                            return (
                                                <div
                                                    key={insight.id}
                                                    onClick={() => toggleInsightSelection(insight.id)}
                                                    className="bg-white rounded-[16px] border border-gray-200 hover:border-gray-300 transition-all shadow-sm cursor-pointer"
                                                >
                                                    <div className="p-5 flex items-stretch">
                                                        {/* Checkbox */}
                                                        <div className="flex-shrink-0 pt-1 pr-4">
                                                            <div className={`w-5 h-5 rounded-md border flex items-center justify-center transition-all ${
                                                                isSelected ? 'bg-primary-600 border-primary-600' : 'border-gray-300 bg-white'
                                                            }`}>
                                                                {isSelected && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                                                            </div>
                                                        </div>

                                                        {/* Details Column 1: Title and Description */}
                                                        <div className="flex-1 pr-6 flex flex-col justify-center">
                                                            <div className="flex items-center flex-wrap gap-2 mb-2">
                                                                <h3 className="font-bold text-base text-gray-900">{insight.name}</h3>
                                                                {hasQueries ? (
                                                                    <span className="px-2 py-0.5 text-xs uppercase font-bold rounded bg-green-100 text-green-700">
                                                                        {t('insightCard.queryCount', { count: queryCount })}
                                                                    </span>
                                                                ) : (
                                                                    <span className="px-2 py-0.5 text-xs uppercase font-bold rounded bg-gray-100 text-gray-500">
                                                                        {t('insightCard.noQuery')}
                                                                    </span>
                                                                )}
                                                                <span className={`px-2 py-0.5 text-xs uppercase font-bold rounded border bg-transparent ${priorityBadge.text} ${priorityBadge.border}`}>
                                                                    {priorityBadge.label}
                                                                </span>
                                                                {categoryBadge && (
                                                                    <span className={`px-2 py-0.5 text-xs uppercase font-bold rounded border bg-transparent ${categoryBadge.text} ${categoryBadge.border}`}>
                                                                        {categoryBadge.label}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p className="text-sm text-gray-500 leading-relaxed max-w-lg">
                                                                {insight.description}
                                                            </p>
                                                        </div>

                                                        {/* Details Column 2: Why It Matters */}
                                                        <div className="w-[450px] flex-shrink-0 px-6 border-l border-gray-100 flex flex-col justify-center">
                                                            {insight.why_it_matters ? (
                                                                <div className="bg-primary-50 border border-primary-200/50 rounded-xl p-3 h-full flex flex-col justify-center">
                                                                    <p className="text-xs font-bold text-primary-700 mb-1">{t('insightCard.whyItMatters')}</p>
                                                                    <p className="text-xs text-primary-700 leading-snug">{insight.why_it_matters}</p>
                                                                </div>
                                                            ) : (
                                                                <div className="h-full flex items-center text-gray-400 text-sm italic">
                                                                    {t('insightCard.noBusinessInsight')}
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Details Column 3: Actions */}
                                                        <div className="w-[220px] flex-shrink-0 pl-6 border-l border-gray-100 flex items-center justify-between">
                                                            <div className="flex flex-col flex-1 items-end gap-2 pr-4 min-w-0">
                                                                {hasQueries ? (
                                                                    <>
                                                                        <span className="text-xs text-gray-500 pb-1 text-right whitespace-nowrap">
                                                                            {t('insightCard.lastSync', { date: insight.created_at ? new Date(insight.created_at).toLocaleDateString(dateLocale, { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A' })}
                                                                        </span>
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                navigate(`/agents/queries/${agentId}`, {
                                                                                    state: { agentName: displayAgentName, databaseId, selectedInsightIds: [insight.id] }
                                                                                });
                                                                            }}
                                                                            className="w-full py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 font-semibold text-xs transition-colors flex items-center justify-center gap-2"
                                                                        >
                                                                            <Activity className="w-3.5 h-3.5" />
                                                                            <span>{t('insightCard.viewQueries')}</span>
                                                                        </button>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <span className="text-xs text-gray-500 pb-1 text-right whitespace-nowrap">
                                                                            {t('insightCard.queryNotGenerated')}
                                                                        </span>
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                if (selectedInsightIds.includes(insight.id) && selectedInsightIds.length > 1) {
                                                                                    handleGenerateQueries(selectedInsightIds);
                                                                                } else {
                                                                                    setSelectedInsightIds([insight.id]);
                                                                                    handleGenerateQueries([insight.id]);
                                                                                }
                                                                            }}
                                                                            disabled={loadingQueries}
                                                                            className={`w-full py-2 bg-primary-600 text-white rounded-lg font-semibold text-xs transition-colors flex items-center justify-center gap-2 ${
                                                                                generatingInsightIds.includes(insight.id) ? 'opacity-50 cursor-not-allowed' : (loadingQueries ? 'cursor-not-allowed' : 'hover:bg-primary-700')
                                                                            }`}
                                                                        >
                                                                            {generatingInsightIds.includes(insight.id) ? (
                                                                                <Loader className="w-3.5 h-3.5 animate-spin" />
                                                                            ) : (
                                                                                <Code className="w-3.5 h-3.5" />
                                                                            )}
                                                                            <span>Generate Queries</span>
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    openDeleteInsightModal(insight);
                                                                }}
                                                                className="flex-shrink-0 p-2 border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200 hover:bg-red-50 rounded-lg transition-colors"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        /* Sub Agent Report Tab - Embed the report page component */
                        <div className="h-full pr-2">
                            <SubAgentReportTab 
                                key={reportRefreshKey} 
                                agentId={agentId} 
                                databaseId={databaseId} 
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* NLP Insight Generation Modal */}
            {showInsightNLPModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-2xl w-full">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                                        <Plus className="w-5 h-5 text-primary-600" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-gray-900">Add Insight</h3>
                                        <p className="text-xs text-gray-500">Create a new insight for {displayAgentName}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={closeInsightNLPModal}
                                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="mb-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Describe the insight you want
                                    </label>
                                    <textarea
                                        value={insightNLPInput}
                                        onChange={handleInsightNLPInputChange}
                                        placeholder="Example: I want to analyze customer churn rate by subscription tier over the last 6 months"
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm resize-none overflow-hidden custom-scrollbar"
                                        rows="4"
                                        style={{ minHeight: '100px', maxHeight: '300px' }}
                                        autoFocus
                                    />
                                    <p className="text-xs text-gray-500 mt-2">
                                        The AI will generate a structured insight based on your description.
                                    </p>
                                </div>
                            </div>

                            <div className="flex gap-3 justify-end">
                                <button
                                    onClick={closeInsightNLPModal}
                                    disabled={generatingInsightNLP}
                                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleGenerateInsightFromNLP}
                                    disabled={generatingInsightNLP || !insightNLPInput.trim()}
                                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    {generatingInsightNLP ? (
                                        <>
                                            <Loader className="w-4 h-4 animate-spin" />
                                            Generating...
                                        </>
                                    ) : (
                                        <>
                                            <Sparkles className="w-4 h-4" />
                                            Add Insight
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation Modal for Delete Insight */}
            <ConfirmationModal
                isOpen={deleteInsightConfirmModal.isOpen}
                onClose={closeDeleteInsightModal}
                onConfirm={confirmDeleteInsight}
                title="Delete Insight"
                message="Are you sure you want to delete this insight? This will also remove any associated queries."
                confirmText="Delete"
                cancelText="Cancel"
            />

            {/* Execute Queries Modal */}
            {showExecuteModal && (
                <ParameterInputModal
                    isOpen={showExecuteModal}
                    onClose={() => setShowExecuteModal(false)}
                    queries={queriesToExecute}
                    onExecute={handleExecuteAllQueries}
                    loading={executingQueries}
                />
            )}
        </div>
    );
};
/**
 * SubAgentReportTab - Component for displaying comprehensive analyses with navigation
 * Shows multiple reports from the current sub-agent with arrow navigation
 */
const SubAgentReportTab = ({ agentId, databaseId }) => {
    const [loading, setLoading] = useState(true);
    const [allReports, setAllReports] = useState([]);
    const [currentReportIndex, setCurrentReportIndex] = useState(0);
    const [expandedSections, setExpandedSections] = useState({});
    const [showParamsModal, setShowParamsModal] = useState(false);
    const { showToast } = useToast();

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const isExpanded = (section) => expandedSections[section] ?? true;

    const getImpactBadge = (impact) => {
        switch (impact?.toUpperCase()) {
            case 'HIGH':
            case 'CRITICAL':
            case 'P0 (CRITICAL)':
                return 'bg-red-100 text-red-800 border-red-200';
            case 'MEDIUM':
            case 'P1 (HIGH)':
                return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'LOW':
            case 'P2 (MEDIUM)':
            case 'P3 (LOW)':
                return 'bg-green-100 text-green-800 border-green-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const getDirectionIcon = (direction) => {
        switch (direction?.toUpperCase()) {
            case 'INCREASING':
                return '↑';
            case 'DECREASING':
                return '↓';
            case 'STABLE':
                return '→';
            default:
                return '↔';
        }
    };

    // Navigate to previous report
    const goToPreviousReport = useCallback(() => {
        setCurrentReportIndex(prev => 
            prev > 0 ? prev - 1 : allReports.length - 1
        );
    }, [allReports.length]);

    // Navigate to next report
    const goToNextReport = useCallback(() => {
        setCurrentReportIndex(prev => 
            prev < allReports.length - 1 ? prev + 1 : 0
        );
    }, [allReports.length]);

    // Keyboard navigation for arrow keys
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Only handle if not typing in an input
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
            
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                if (allReports.length > 1) goToPreviousReport();
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                if (allReports.length > 1) goToNextReport();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [allReports.length, goToPreviousReport, goToNextReport]);

    useEffect(() => {
        const fetchAllAnalyses = async () => {
            if (!agentId) return;

            setLoading(true);
            try {
                const { getSubAgentAnalyses } = await import('../services/agentApi');
                // Fetch all analyses for this agent (limit 100)
                const response = await getSubAgentAnalyses(agentId, databaseId, 100);

                if (response.data?.status === 'success') {
                    const analyses = response.data.data.analyses || [];
                    
                    // Transform all analyses
                    const transformedReports = analyses.map(analysis => {
                        const responseData = analysis.response_data || {};
                        const executionSummary = responseData.execution_summary || analysis.execution_summary || {};

                        return {
                            ...analysis,
                            agent_name: analysis.agent_name || responseData.agent_name || 'Sub Agent',
                            report_content: analysis.report_content || responseData.report_content || null,
                            comprehensive_analysis: responseData.comprehensive_analysis || analysis.comprehensive_analysis,
                            parameters_used: responseData.parameters_used || analysis.parameters_used || {},
                            queries_count: executionSummary.queries_executed || 0,
                            successful_queries: executionSummary.successful_queries || 0,
                            failed_queries: executionSummary.failed_queries || 0,
                            insights_count: executionSummary.insights_executed || 0
                        };
                    });
                    
                    setAllReports(transformedReports);
                    setCurrentReportIndex(0); // Show most recent first
                }
            } catch (error) {
                console.error('Failed to fetch analysis:', error);
                showToast('Failed to load reports', 'error');
            } finally {
                setLoading(false);
            }
        };

        fetchAllAnalyses();
    }, [agentId, databaseId, showToast]);

    const currentReport = allReports[currentReportIndex];

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading sub-agent report...</p>
                </div>
            </div>
        );
    }

    if (allReports.length === 0) {
        return (
            <div className="max-w-2xl mx-auto p-8">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                    <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <BarChart3 className="w-12 h-12 text-gray-400" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">No Sub-Agent Report Available</h2>
                    <p className="text-gray-600 mb-6">
                        No comprehensive analysis has been generated yet. Go to the Insights List tab and click "Generate Report" to create one.
                    </p>
                </div>
            </div>
        );
    }

    const {
        comprehensive_analysis,
        queries_count = 0,
        successful_queries = 0,
        failed_queries = 0,
        insights_count = 0,
        parameters_used,
        created_at,
        agent_name
    } = currentReport || {};

    // Flatten parameters
    const flattenParameters = (params) => {
        if (!params) return {};
        if (typeof params !== 'object') return {};
        const flattened = {};
        Object.values(params).forEach(queryParams => {
            if (typeof queryParams === 'object' && queryParams !== null) {
                Object.assign(flattened, queryParams);
            }
        });
        return flattened;
    };

    const flattenedParams = flattenParameters(parameters_used);
    const hasMultipleReports = allReports.length > 1;

    return (
        <div className="page-content overflow-y-auto">
            {/* Header with Navigation */}
            <div className="mb-6">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <h2 className="text-2xl font-bold text-gray-900">{agent_name || 'Sub Agent'} Report</h2>
                            {hasMultipleReports && (
                                <span className="px-3 py-1 bg-primary-100 text-primary-700 text-sm font-semibold rounded-full">
                                    Report {currentReportIndex + 1} of {allReports.length}
                                </span>
                            )}
                        </div>
                        <p className="text-gray-600">AI-powered business intelligence from {queries_count} queries across {insights_count} insights</p>
                        {created_at && (
                            <p className="text-sm text-gray-500 mt-1">
                                Generated: {new Date(created_at).toLocaleDateString('en-GB', {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                })}
                            </p>
                        )}
                    </div>
                    {hasMultipleReports && (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={goToPreviousReport}
                                className="p-2.5 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
                                title="Previous report (Left Arrow)"
                            >
                                <ChevronDown className="w-5 h-5 text-gray-600 rotate-90" />
                            </button>
                            <button
                                onClick={goToNextReport}
                                className="p-2.5 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
                                title="Next report (Right Arrow)"
                            >
                                <ChevronDown className="w-5 h-5 text-gray-600 -rotate-90" />
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Execution Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl border border-primary-200 p-5">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-primary-700 mb-1">Queries Executed</p>
                            <p className="text-3xl font-bold text-primary-900">{queries_count}</p>
                        </div>
                        <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm">
                            <BarChart3 className="w-6 h-6 text-primary-600" />
                        </div>
                    </div>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200 p-5">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-green-700 mb-1">Successful</p>
                            <p className="text-3xl font-bold text-green-900">{successful_queries}</p>
                        </div>
                        <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm">
                            <CheckCircle className="w-6 h-6 text-green-600" />
                        </div>
                    </div>
                </div>
                <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl border border-red-200 p-5">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-red-700 mb-1">Failed</p>
                            <p className="text-3xl font-bold text-red-900">{failed_queries}</p>
                        </div>
                        <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm">
                            <AlertCircle className="w-6 h-6 text-red-600" />
                        </div>
                    </div>
                </div>
                <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl border border-primary-200 p-5">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-primary-700 mb-1">Insights Analyzed</p>
                            <p className="text-3xl font-bold text-primary-900">{insights_count}</p>
                        </div>
                        <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm">
                            <Lightbulb className="w-6 h-6 text-primary-600" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Parameters Button - Icon below execution summary */}
            {flattenedParams && Object.keys(flattenedParams).length > 0 && (
                <div className="flex justify-end mb-4">
                    <button
                        onClick={() => setShowParamsModal(true)}
                        className="p-2.5 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 transition-colors shadow-sm group"
                        title="View Input Parameters"
                    >
                        <Info className="w-5 h-5 text-primary-600 group-hover:text-primary-700" />
                    </button>
                </div>
            )}

            {/* Full HTML Report with Charts */}
            {currentReport.report_content && (
                <div className="mb-6">
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                        <DynamicIframe htmlContent={currentReport.report_content} />
                    </div>
                </div>
            )}

            {/* Parameters Modal */}
            <ParametersModal
                isOpen={showParamsModal}
                onClose={() => setShowParamsModal(false)}
                parameters={flattenedParams}
            />

            {/* Executive Summary */}
            {comprehensive_analysis?.executive_summary && (
                <div className="bg-gradient-to-r from-primary-50 to-primary-50 border border-primary-200 rounded-xl p-6 mb-6">
                    <div className="flex items-start space-x-3">
                        <div className="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Lightbulb className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                            <h3 className="text-xl font-bold text-primary-900 mb-3">Executive Summary</h3>
                            <p className="text-primary-800 text-base leading-relaxed">
                                {comprehensive_analysis.executive_summary}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Key Metrics Summary */}
            {comprehensive_analysis?.key_metrics_summary && Object.keys(comprehensive_analysis.key_metrics_summary).length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 shadow-sm">
                    <div className="flex items-center space-x-2 mb-4">
                        <BarChart3 className="w-6 h-6 text-primary-600" />
                        <h2 className="text-xl font-bold text-gray-900">Key Metrics</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                        {Object.entries(comprehensive_analysis.key_metrics_summary).map(([key, value]) => (
                            <div key={key} className="bg-gray-50 rounded-lg p-4 text-center">
                                <p className="text-xs text-gray-600 mb-1 capitalize">{key.replace(/_/g, ' ')}</p>
                                <p className="text-lg font-bold text-gray-900">{value}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Detailed Findings */}
            {comprehensive_analysis?.detailed_findings && comprehensive_analysis.detailed_findings.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-2">
                            <Target className="w-6 h-6 text-primary-600" />
                            <h2 className="text-xl font-bold text-gray-900">Detailed Findings</h2>
                        </div>
                        <button
                            onClick={() => toggleSection('findings')}
                            className="text-gray-500 hover:text-gray-700"
                        >
                            {isExpanded('findings') ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </button>
                    </div>
                    {isExpanded('findings') && (
                        <div className="space-y-4">
                            {comprehensive_analysis.detailed_findings.map((finding, idx) => (
                                <div key={idx} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                                    <div className="flex items-start justify-between mb-2">
                                        <h3 className="font-semibold text-gray-900">{finding.title}</h3>
                                        <span className={`px-2.5 py-1 text-xs font-medium rounded-full border ${getImpactBadge(finding.impact)}`}>
                                            {finding.impact}
                                        </span>
                                    </div>
                                    <p className="text-gray-700 text-sm mb-3">{finding.description}</p>
                                    {finding.supporting_data && Object.keys(finding.supporting_data).length > 0 && (
                                        <div className="bg-gray-50 rounded p-3">
                                            <p className="text-xs font-medium text-gray-600 mb-2">Supporting Data:</p>
                                            <div className="grid grid-cols-2 gap-2">
                                                {Object.entries(finding.supporting_data).map(([k, v]) => (
                                                    <div key={k} className="text-sm">
                                                        <span className="text-gray-600 capitalize">{k.replace(/_/g, ' ')}:</span>
                                                        <span className="font-semibold text-gray-900 ml-2">{v}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Cross-Query Insights */}
            {comprehensive_analysis?.cross_query_insights && comprehensive_analysis.cross_query_insights.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 shadow-sm">
                    <div className="flex items-center space-x-2 mb-4">
                        <Activity className="w-6 h-6 text-primary-600" />
                        <h2 className="text-xl font-bold text-gray-900">Cross-Query Insights</h2>
                    </div>
                    <div className="space-y-4">
                        {comprehensive_analysis.cross_query_insights.map((insight, idx) => (
                            <div key={idx} className="border-l-4 border-primary-500 pl-4 py-2 bg-primary-50 rounded-r-lg">
                                <p className="text-gray-800 mb-2">{insight.insight}</p>
                                <div className="flex items-center gap-2 text-xs">
                                    <span className="text-gray-600">Connected:</span>
                                    <div className="flex flex-wrap gap-1">
                                        {insight.queries_connected.map((q, i) => (
                                            <span key={i} className="px-2 py-0.5 bg-primary-200 text-primary-800 rounded text-xs">
                                                {q}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                <p className="text-sm text-gray-700 mt-2"><span className="font-medium">Significance:</span> {insight.significance}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Trends Identified */}
            {comprehensive_analysis?.trends_identified && comprehensive_analysis.trends_identified.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 shadow-sm">
                    <div className="flex items-center space-x-2 mb-4">
                        <TrendingUp className="w-6 h-6 text-green-600" />
                        <h2 className="text-xl font-bold text-gray-900">Trends Identified</h2>
                    </div>
                    <div className="space-y-3">
                        {comprehensive_analysis.trends_identified.map((trend, idx) => (
                            <div key={idx} className="border border-gray-200 rounded-lg p-4">
                                <div className="flex items-start justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <span className="text-xl font-bold text-green-600">
                                            {getDirectionIcon(trend.direction)}
                                        </span>
                                        <p className="text-gray-800 font-medium">{trend.trend}</p>
                                    </div>
                                </div>
                                <div className="flex gap-3 text-xs mt-2">
                                    <span className="px-2 py-1 bg-primary-100 text-primary-800 rounded">{trend.direction}</span>
                                    <span className="px-2 py-1 bg-primary-100 text-primary-800 rounded">{trend.magnitude}</span>
                                </div>
                                <p className="text-sm text-gray-600 mt-2"><span className="font-medium">Evidence:</span> {trend.evidence}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Anomalies and Outliers */}
            {comprehensive_analysis?.anomalies_and_outliers && comprehensive_analysis.anomalies_and_outliers.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 shadow-sm">
                    <div className="flex items-center space-x-2 mb-4">
                        <AlertTriangle className="w-6 h-6 text-red-600" />
                        <h2 className="text-xl font-bold text-gray-900">Anomalies & Outliers</h2>
                    </div>
                    <div className="space-y-4">
                        {comprehensive_analysis.anomalies_and_outliers.map((anomaly, idx) => (
                            <div key={idx} className="border border-red-200 bg-red-50 rounded-lg p-4">
                                <div className="flex items-start justify-between mb-2">
                                    <p className="text-gray-900 font-medium">{anomaly.anomaly}</p>
                                    <span className={`px-2.5 py-1 text-xs font-medium rounded-full border ${getImpactBadge(anomaly.severity)}`}>
                                        {anomaly.severity}
                                    </span>
                                </div>
                                <p className="text-sm text-gray-700 mt-1"><span className="font-medium">Possible Cause:</span> {anomaly.possible_cause}</p>
                                <p className="text-sm text-gray-700 mt-1"><span className="font-medium">Recommended Action:</span> {anomaly.recommended_action}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Strategic Recommendations */}
            {comprehensive_analysis?.strategic_recommendations && comprehensive_analysis.strategic_recommendations.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-2">
                            <Target className="w-6 h-6 text-primary-600" />
                            <h2 className="text-xl font-bold text-gray-900">Strategic Recommendations</h2>
                        </div>
                        <button
                            onClick={() => toggleSection('recommendations')}
                            className="text-gray-500 hover:text-gray-700"
                        >
                            {isExpanded('recommendations') ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </button>
                    </div>
                    {isExpanded('recommendations') && (
                        <div className="space-y-4">
                            {comprehensive_analysis.strategic_recommendations.map((rec, idx) => (
                                <div key={idx} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                                    <p className="text-gray-900 font-medium mb-3">{rec.recommendation}</p>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                                        <div>
                                            <span className="text-gray-600">Priority:</span>
                                            <span className={`ml-2 px-2 py-1 rounded ${getImpactBadge(rec.priority)}`}>
                                                {rec.priority}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Expected Impact:</span>
                                            <p className="font-medium text-gray-900">{rec.expected_impact}</p>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Complexity:</span>
                                            <span className="ml-2 px-2 py-1 bg-gray-100 text-gray-800 rounded">
                                                {rec.implementation_complexity}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Timeline:</span>
                                            <span className="ml-2 px-2 py-1 bg-primary-100 text-primary-800 rounded">
                                                {rec.timeline}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Business Impact Assessment */}
            {comprehensive_analysis?.business_impact_assessment && (
                <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 shadow-sm">
                    <div className="flex items-center space-x-2 mb-4">
                        <DollarSign className="w-6 h-6 text-green-600" />
                        <h2 className="text-xl font-bold text-gray-900">Business Impact Assessment</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                        {comprehensive_analysis.business_impact_assessment.revenue_impact && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <p className="text-xs text-green-700 font-medium mb-1">Revenue Impact</p>
                                <p className="text-gray-900">{comprehensive_analysis.business_impact_assessment.revenue_impact}</p>
                            </div>
                        )}
                        {comprehensive_analysis.business_impact_assessment.cost_impact && (
                            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                                <p className="text-xs text-primary-700 font-medium mb-1">Cost Impact</p>
                                <p className="text-gray-900">{comprehensive_analysis.business_impact_assessment.cost_impact}</p>
                            </div>
                        )}
                        {comprehensive_analysis.business_impact_assessment.efficiency_impact && (
                            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                                <p className="text-xs text-primary-700 font-medium mb-1">Efficiency Impact</p>
                                <p className="text-gray-900">{comprehensive_analysis.business_impact_assessment.efficiency_impact}</p>
                            </div>
                        )}
                        {comprehensive_analysis.business_impact_assessment.risk_impact && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                <p className="text-xs text-yellow-700 font-medium mb-1">Risk Impact</p>
                                <p className="text-gray-900">{comprehensive_analysis.business_impact_assessment.risk_impact}</p>
                            </div>
                        )}
                        {comprehensive_analysis.business_impact_assessment.customer_impact && (
                            <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
                                <p className="text-xs text-pink-700 font-medium mb-1">Customer Impact</p>
                                <p className="text-gray-900">{comprehensive_analysis.business_impact_assessment.customer_impact}</p>
                            </div>
                        )}
                    </div>
                    {comprehensive_analysis.business_impact_assessment.overall_assessment && (
                        <div className="bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-lg p-4">
                            <p className="text-sm font-medium text-gray-700 mb-1">Overall Assessment</p>
                            <p className="text-gray-900">{comprehensive_analysis.business_impact_assessment.overall_assessment}</p>
                        </div>
                    )}
                </div>
            )}

            {/* Next Steps */}
            {comprehensive_analysis?.next_steps && comprehensive_analysis.next_steps.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 shadow-sm">
                    <div className="flex items-center space-x-2 mb-4">
                        <Clock className="w-6 h-6 text-primary-600" />
                        <h2 className="text-xl font-bold text-gray-900">Recommended Next Steps</h2>
                    </div>
                    <div className="space-y-3">
                        {comprehensive_analysis.next_steps.map((step, idx) => (
                            <div key={idx} className="flex items-start gap-3 border border-gray-200 rounded-lg p-4">
                                <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm">
                                    {idx + 1}
                                </div>
                                <div className="flex-1">
                                    <p className="text-gray-900 font-medium">{step.action}</p>
                                    <div className="flex gap-3 mt-2 text-xs">
                                        <span className="text-gray-600">
                                            <Users className="w-3 h-3 inline mr-1" />
                                            Owner: {step.owner}
                                        </span>
                                        <span className="text-gray-600">
                                            <Clock className="w-3 h-3 inline mr-1" />
                                            Deadline: {step.deadline}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Data Quality Notes */}
            {comprehensive_analysis?.data_quality_notes && comprehensive_analysis.data_quality_notes.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mb-6">
                    <div className="flex items-center space-x-2 mb-3">
                        <AlertCircle className="w-6 h-6 text-yellow-600" />
                        <h2 className="text-lg font-bold text-yellow-900">Data Quality Notes</h2>
                    </div>
                    <ul className="space-y-2">
                        {comprehensive_analysis.data_quality_notes.map((note, idx) => (
                            <li key={idx} className="text-sm text-yellow-800 flex items-start gap-2">
                                <span className="text-yellow-600 mt-1">•</span>
                                <span>{note}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};
